import { CheckCircle2, GraduationCap } from 'lucide-react';
import { Card, CardHeader } from '../ui/Card';
import { CollapsibleSection } from '../ui/CollapsibleSection';
import { TrainingTaskRow } from './TrainingTaskRow';
import type { OnboardingTask } from '../onboarding/OnboardingTasks';

/**
 * The inductee's training modules on /me, as their own section rather than
 * buried among the onboarding paperwork: training is a different kind of work
 * (sit down and do a course, not fill in a form), and mixing the two made the
 * onboarding count read as if there were far more paperwork than there is.
 *
 * Same shape and behaviour as OnboardingTasks, including collapsing to a
 * single line once everything is done.
 */
export function TrainingTasks({ tasks }: { tasks: OnboardingTask[] }) {
  const training = tasks.filter((t) => t.onboarding_kind === 'training');
  if (training.length === 0) return null;

  // Same reading as onboarding: submitted-and-with-HR is off the learner's
  // plate. A training module is either done or not, but an admin can send one
  // back for review, so the awaiting state is handled the same way.
  const done = training.filter(
    (t) => t.status === 'complete' || t.status === 'awaiting_review',
  ).length;

  const rows = (
    <div className="divide-y divide-divider/10">
      {training.map((t) => (
        <TrainingTaskRow key={t.task_id} task={t} />
      ))}
    </div>
  );

  if (done === training.length) {
    return (
      <CollapsibleSection
        icon={<CheckCircle2 className="h-4 w-4 text-success" />}
        title="Training complete"
        description="You've finished every module assigned to you. Expand to download your certificates."
        trailing={`${done} of ${training.length}`}
      >
        {rows}
      </CollapsibleSection>
    );
  }

  return (
    <Card>
      <CardHeader
        icon={<GraduationCap className="h-4 w-4" />}
        title="Your training"
        description={`${done} of ${training.length} complete`}
      />
      {rows}
    </Card>
  );
}
